import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, CheckCircle } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { fallbackService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { FallbackRequest } from '../types/fallback';
import { Form, FormField, FormLabel, FormTextarea, Button, Alert } from '../components/ui';
import LoadingSpinner from '../components/ui/LoadingSpinner';

function FallbackResponse() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [humanResponse, setHumanResponse] = useState('');

  // Add meta tags to prevent link previews
  useEffect(() => {
    // Create meta tags to prevent link previews
    const metaTags = [
      { name: 'robots', content: 'noindex, nofollow' },
      { name: 'description', content: '' },
      { property: 'og:title', content: 'Resposta de Fallback' },
      { property: 'og:description', content: '' },
      { property: 'og:image', content: '' },
      { name: 'twitter:card', content: 'none' },
    ];

    // Add meta tags to head
    const addedTags = metaTags.map(tagInfo => {
      const tag = document.createElement('meta');
      Object.entries(tagInfo).forEach(([attr, value]) => {
        tag.setAttribute(attr, value);
      });
      document.head.appendChild(tag);
      return tag;
    });

    // Clean up function to remove added tags
    return () => {
      addedTags.forEach(tag => {
        if (tag.parentNode) {
          tag.parentNode.removeChild(tag);
        }
      });
    };
  }, []);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/fallback-response/${id}` } });
    }
  }, [isAuthenticated, navigate, id]);

  // Fetch the fallback request
  const {
    data: request,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['fallback-request', id],
    queryFn: () => fallbackService.getRequest(Number(id)),
    enabled: isAuthenticated && !!id,
    retry: 1,
  });

  // Mutation to update the request
  const updateMutation = useMutation({
    mutationFn: (data: Partial<FallbackRequest>) => fallbackService.updateRequest(Number(id), data),
    onSuccess: () => {
      messageHandler.success('Resposta enviada com sucesso!');
      // Close the window/tab after successful submission
      window.close();

      // Fallback if window.close() doesn't work (some browsers block it)
      setTimeout(() => {
        navigate('/fallback-requests');
      }, 1000);
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!humanResponse.trim()) {
      messageHandler.error('Por favor, forneça uma resposta.');
      return;
    }

    if (!request) return;

    updateMutation.mutate({
      ...request,
      human_response: humanResponse,
      status: 'awaiting_finalization',
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 p-4">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg max-w-md w-full">
          <div className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-yellow-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Autenticação Necessária
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Você precisa estar autenticado para responder a esta solicitação.
            </p>
            <Button
              onClick={() => navigate('/login', { state: { from: `/fallback-response/${id}` } })}
            >
              Fazer Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 p-4">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg max-w-md w-full">
          <div className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Erro</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Não foi possível carregar a solicitação. Ela pode não existir ou você não tem
              permissão para acessá-la.
            </p>
            <Button onClick={() => window.close()}>Fechar</Button>
          </div>
        </div>
      </div>
    );
  }

  // Check if the request is already processed or not pending
  const isAlreadyProcessed = request.status !== 'pending';

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Responder Solicitação de Fallback
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              ID: {request.id} | Assistente: {request.assistant_name}
            </p>
          </div>

          {isAlreadyProcessed ? (
            <div className="p-6">
              <Alert
                variant={request.status === 'completed' ? 'success' : 'warning'}
                icon={request.status === 'completed' ? CheckCircle : AlertTriangle}
              >
                <h3 className="text-sm font-medium">
                  Esta solicitação já foi{' '}
                  {request.status === 'completed' ? 'processada' : 'respondida'}
                </h3>
                <p className="mt-2 text-sm">
                  {request.status === 'completed'
                    ? 'Esta solicitação já foi processada e finalizada.'
                    : 'Esta solicitação já foi respondida e está aguardando finalização.'}
                </p>
              </Alert>

              <div className="mt-6 space-y-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Pergunta:
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {request.question}
                  </p>
                </div>

                {request.human_response && (
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Resposta Humana:
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {request.human_response}
                    </p>
                  </div>
                )}

                {request.final_response && (
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Resposta Final:
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {request.final_response}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6">
                <Button onClick={() => window.close()}>Fechar</Button>
              </div>
            </div>
          ) : (
            <Form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Pergunta:
                </h3>
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {request.question}
                </p>
              </div>

              {request.contact && (
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Contato:
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    {request.contact_name ? (
                      <>
                        {request.contact_name}
                        {request.contact && (
                          <span className="text-xs text-gray-500 ml-2">({request.contact})</span>
                        )}
                      </>
                    ) : (
                      request.contact
                    )}
                  </p>
                </div>
              )}

              <FormField>
                <FormLabel htmlFor="human_response" required>
                  Sua Resposta
                </FormLabel>
                <FormTextarea
                  id="human_response"
                  name="human_response"
                  value={humanResponse}
                  onChange={e => setHumanResponse(e.target.value)}
                  required
                  rows={6}
                  placeholder="Digite sua resposta aqui..."
                />
              </FormField>

              <div className="flex justify-end space-x-3">
                <Button type="button" variant="secondary" onClick={() => window.close()}>
                  Cancelar
                </Button>
                <Button type="submit" isLoading={updateMutation.isPending}>
                  Enviar Resposta
                </Button>
              </div>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
}

export default FallbackResponse;
